<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useDropTarget } from '../../logic/drag-drop/useDropTarget';
import {
  isNativeFileDrag,
  useNativeFileDropTarget,
} from '../../logic/drag-drop/useNativeFileDropTarget';
import type { FileSystemItem } from '../../types';

const props = withDefaults(
  defineProps<{
    /** The path this zone represents as a drop target. */
    path: string;
    /**
     * The materialized item (if any) this zone wraps. When present, its id/driveId let a move
     * onto this zone skip the destination getItemReference() round-trip; path-only zones
     * (breadcrumbs) omit it and fall back to path resolution in the mutation.
     */
    item?: FileSystemItem;
    /**
     * Also wire up OS-file (upload) dropping on this zone, combined into the same
     * `isDragOver`/handlers exposed to the slot. Defaults to internal-item-move only.
     */
    acceptsUpload?: boolean;
    /**
     * When true, this zone never accepts drops: no highlight, enter/leave/drop are no-ops, and
     * `onDragOver` skips `preventDefault()` so the browser shows its native "not allowed" cursor.
     * For a list of mixed items where only some (e.g. directories, not files) are valid targets.
     */
    disabled?: boolean;
  }>(),
  { acceptsUpload: false, disabled: false },
);

defineSlots<{
  default(props: {
    /** Either kind of drag is over this zone — the combined highlight most consumers use. */
    isDragOver: boolean;
    /** Only an internal item move is over this zone. */
    isMoveDragOver: boolean;
    /** Only a native OS-file (upload) drag is over this zone. Split out so consumers can render a
     * distinct indicator (e.g. the file-explorer folder's dashed drop border). */
    isUploadDragOver: boolean;
    onDragEnter: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    /**
     * Must be bound by every consumer — a bare `@dragover.prevent` is not equivalent and never was
     * for a `disabled` zone. It is also what writes `dataTransfer.dropEffect`, which is the only
     * thing that tells the OS whether this zone will really take the drop: for an OS file drag on a
     * zone that cannot accept one, this is what shows a not-allowed cursor instead of inviting a
     * drop that would be silently discarded.
     */
    onDragOver: (event: DragEvent) => void;
    onDrop: (event: DragEvent) => void;
  }): unknown;
}>();

const path = toRef(props, 'path');
const item = toRef(props, 'item');

const {
  isDragOver: isMoveDragOverInner,
  dropEffect,
  onDragEnter: onMoveDragEnter,
  onDragLeave: onMoveDragLeave,
  onDrop: onMoveDrop,
} = useDropTarget(path, item);

// Only instantiated when actually needed: `acceptsUpload` is a static per-usage decision at every
// call site today (never toggled at runtime), so reading it once here to decide whether to run
// useNativeFileDropTarget at all is safe, and avoids spinning up its upload mutation/refs for the
// large majority of DropZone instances (breadcrumb segments, sidebar tree rows) that never accept
// uploads.
const upload = props.acceptsUpload ? useNativeFileDropTarget(path) : undefined;

const isMoveDragOver = computed(() => !props.disabled && isMoveDragOverInner.value);
const isUploadDragOver = computed(() => !props.disabled && (upload?.isDragOver.value ?? false));
const isDragOver = computed(() => isMoveDragOver.value || isUploadDragOver.value);

// Combined into single handlers (rather than Vue's array-handler syntax) because a modifier like
// `.prevent` wraps the WHOLE bound value in `withModifiers`, which calls the array itself as a
// function (`fn(event)`) once modifiers pass — arrays aren't callable, so that throws and silently
// drops both handlers for that event. Centralizing the combine here means no consumer can
// reintroduce that bug by hand.
const onDragEnter = (event: DragEvent) => {
  if (props.disabled) return;
  onMoveDragEnter();
  upload?.onDragEnter(event);
};
const onDragLeave = (event: DragEvent) => {
  if (props.disabled) return;
  onMoveDragLeave(event);
  upload?.onDragLeave(event);
};
const onDragOver = (event: DragEvent) => {
  if (props.disabled) return;
  // The two drag species are answered separately, because the question differs: for an internal move
  // it is "which verb would this be", for an OS file drag it is "can this zone take an upload at
  // all". useDropTarget's dropEffect is null for the latter (no draggedItems), which is why it
  // cannot answer for both.
  if (isNativeFileDrag(event)) {
    // A move-only zone (`acceptsUpload: false` — breadcrumbs, and any zone that opts out) has no
    // upload half to ask, and the answer is a flat no: it accepts nothing from the OS, so say so
    // rather than showing a "copy" cursor and dropping the files on the floor.
    if (upload) {
      upload.onDragOver(event);
    } else {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    }
    return;
  }
  event.preventDefault();
  // Narrows the source's 'copyMove' to what this particular zone would actually do, which is what
  // puts the native "+" badge on the cursor over a cross-mount target. Assigned on every dragover
  // so it also overrides a Ctrl/Cmd modifier the browser applied — the verb comes from the transfer
  // policy, never from the keyboard (Ctrl-to-copy is deliberately not a feature).
  if (event.dataTransfer && dropEffect.value) {
    event.dataTransfer.dropEffect = dropEffect.value;
  }
};
const onDrop = (event: DragEvent) => {
  if (props.disabled) return;
  onMoveDrop();
  upload?.onDrop(event);
};
</script>

<template>
  <slot
    :is-drag-over="isDragOver"
    :is-move-drag-over="isMoveDragOver"
    :is-upload-drag-over="isUploadDragOver"
    :on-drag-enter="onDragEnter"
    :on-drag-leave="onDragLeave"
    :on-drag-over="onDragOver"
    :on-drop="onDrop"
  />
</template>
